import React from 'react'
import { Modal, Form, Col, Row, Button } from 'react-bootstrap'
import { createModal, ModalResolveType, useModalAction } from '@src/lib/modal-manager'
import { useFetch } from '@src/hooks'
import { useForm, Controller } from '@src/lib/react-hook-form'
import z from 'zod'
import { UserService } from '@src/domain/services'
import { UserDto } from '@src/domain/repositories/system.repo'

interface EditUserProps {
  dtoUuid?: string;
  itemDto?: UserDto;
}

const initState: UserDto = {
  account: '',
  password: '',
  name: '',
  employeeNo: '',
  email: '',
  mobile: '',
  roleUuid: '',
  status: '1'
}

export function EditUserModalWrapper(props: EditUserProps) {
  const { resolve, remove, hide } = useModalAction()
  const { dtoUuid, itemDto } = props

  const { register, handleSubmit, formState: { errors }, control } = useForm<UserDto>({
    reValidateMode: 'onSubmit',
    defaultValues: {...initState, ...itemDto},
    rules: z.object({
      account: z.string().nonempty({ message: '請輸入帳號' }),
      password: z.string().nonempty({ message: '請輸入密碼' }),
      name: z.string().nonempty({ message: '請名稱' }),
      employeeNo: z.string().optional(),
      email: z.string().optional(),
      mobile: z.string().optional(),
      roleUuid: z.string().nonempty({ message: '請選擇角色' }),
      status: z.string().nonempty({ message: '請選擇狀態' })
    })
  })

  const { data: roleOptions = [] } = useFetch(UserService.getRoles, null)

  const handleClose = () => {
    resolve(ModalResolveType.CLOSED)
    hide()
    remove()
  }

  const handleCancel = () => {
    resolve(ModalResolveType.CANCEL)
    hide()
    remove()
  }

  const onSubmit = (formDto: UserDto) => {
    resolve(ModalResolveType.OK, formDto)
    hide()
    remove()
  }

  return (
    <Modal size='lg' show onHide={handleClose} centered>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header closeButton>
          <Modal.Title>{`${dtoUuid ? '編輯' : '新增'}使用者`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column sm='2'>
            帳號
          </Form.Label>
          <Col sm='10'>
            <Form.Control {...register('account')} />
          </Col>
          {errors.account && <Form.Text className='text-danger'>{errors.account.message}</Form.Text>}
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column sm='2'>
            密碼
          </Form.Label>
          <Col sm='10'>
            <Form.Control type='password' {...register('password')} />
          </Col>
          {errors.password && <Form.Text className='text-danger'>{errors.password.message}</Form.Text>}
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column sm='2'>
            姓名
          </Form.Label>
          <Col sm='10'>
            <Form.Control {...register('name')} />
          </Col>
          {errors.name && <Form.Text className='text-danger'>{errors.name.message}</Form.Text>}
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column sm='2'>
            員工編號
          </Form.Label>
          <Col sm='10'>
            <Form.Control {...register('employeeNo')} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column sm='2'>
            E-Mail
          </Form.Label>
          <Col sm='10'>
            <Form.Control placeholder='email@example.com' {...register('email')} />
          </Col>
          {errors.email && <Form.Text className='text-danger'>{errors.email.message}</Form.Text>}
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column sm='2'>
            電話
          </Form.Label>
          <Col sm='10'>
            <Form.Control {...register('mobile')} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} className='mb-3'>
          <Form.Label column sm='2'>
            角色
          </Form.Label>
          <Col sm='10'>
          <Controller
            name='roleUuid'
            control={control}
            render={({ field }) => <Form.Select {...field}
          >
            <option value=''>請選擇角色</option>
              {roleOptions.map(option => <option key={option.uuid} value={option.uuid}>{option.description}</option>)}
            </Form.Select>}
          />
          </Col>
          {errors.roleUuid && <Form.Text className='text-danger'>{errors.roleUuid.message}</Form.Text>}
        </Form.Group>
          <Form.Group as={Row} className='mb-3'>
            <Form.Label column sm='2'>
              狀態
            </Form.Label>
            <Col sm='10'>
              <Form.Select {...register('status')}>
                <option value='1'>啟用</option>
                <option value='0'>停用</option>
              </Form.Select>
            </Col>
            {errors.status && <Form.Text className='text-danger'>{errors.status.message}</Form.Text>}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCancel} variant='light'>
            取消
          </Button>
          <Button type='submit'>
            確認
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export const EditUserModal = createModal(EditUserModalWrapper)
